import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Bed, Bath, MapPin, Check, Phone, ChevronLeft, ChevronRight, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import api from "../lib/api";
import logger from "../lib/logger";

const PropertyDetailPage = () => {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const data = await api.getProperty(id);
        setProperty(data);
        setFormData((prev) => ({
          ...prev,
          message: `I am interested in the property: ${data.title}`,
        }));
      } catch (error) {
        logger.error("Error fetching property:", error);
        toast.error("Property not found");
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.submitContact({
        ...formData,
        property_id: id,
        submission_type: "property_enquiry",
      });
      toast.success("Enquiry submitted successfully! We'll be in touch soon.");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      toast.error("Failed to submit enquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      maximumFractionDigits: 0,
    }).format(price);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="animate-pulse">
          <div className="h-8 bg-slate-200 rounded w-32 mb-8" />
          <div className="h-96 bg-slate-200 rounded-lg mb-8" />
          <div className="h-64 bg-slate-200 rounded-lg" />
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <h1 className="font-heading text-2xl font-semibold text-navy mb-4">Property Not Found</h1>
        <p className="text-slate-600 mb-6">The property you're looking for doesn't exist or has been removed.</p>
        <Button asChild className="bg-navy hover:bg-navy-light">
          <Link to="/properties">View All Properties</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="py-8 md:py-12" data-testid="property-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link
          to="/properties"
          className="inline-flex items-center text-navy hover:text-gold mb-8 font-medium"
          data-testid="back-to-properties"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Properties
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Image Gallery */}
            <div className="mb-8">
              {/* Main Image */}
              <div 
                className="relative rounded-lg overflow-hidden cursor-pointer"
                onClick={() => setLightboxOpen(true)}
              >
                <img
                  src={property.images?.[selectedImageIndex] || "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=1200"}
                  alt={property.title}
                  className="w-full h-64 sm:h-80 md:h-[500px] object-cover hover:scale-105 transition-transform duration-300"
                />
                {!property.available && (
                  <div className="absolute inset-0 bg-navy/60 flex items-center justify-center pointer-events-none">
                    <span className="text-white font-heading font-semibold text-2xl">Let Agreed</span>
                  </div>
                )}
                <Badge className="absolute top-4 left-4 bg-navy text-white text-sm">
                  {property.property_type}
                </Badge>
                {property.images?.length > 1 && (
                  <div className="absolute bottom-4 right-4 bg-navy/80 text-white px-3 py-1 rounded-full text-sm">
                    {selectedImageIndex + 1} / {property.images.length}
                  </div>
                )}
              </div>
              
              {/* Thumbnail Gallery */}
              {property.images?.length > 1 && (
                <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                  {property.images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImageIndex === index ? 'border-gold' : 'border-transparent hover:border-navy/30'
                      }`}
                    >
                      <img src={img} alt={`View ${index + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Lightbox */}
            {lightboxOpen && (
              <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={() => setLightboxOpen(false)}>
                <button 
                  className="absolute top-4 right-4 text-white hover:text-gold z-10"
                  onClick={() => setLightboxOpen(false)}
                >
                  <X className="w-8 h-8" />
                </button>
                {property.images?.length > 1 && (
                  <>
                    <button 
                      className="absolute left-4 text-white hover:text-gold z-10"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedImageIndex((prev) => (prev === 0 ? property.images.length - 1 : prev - 1));
                      }}
                    >
                      <ChevronLeft className="w-10 h-10" />
                    </button>
                    <button 
                      className="absolute right-4 text-white hover:text-gold z-10"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedImageIndex((prev) => (prev === property.images.length - 1 ? 0 : prev + 1));
                      }}
                    >
                      <ChevronRight className="w-10 h-10" />
                    </button>
                  </>
                )}
                <img
                  src={property.images?.[selectedImageIndex] || "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=1200"}
                  alt={property.title}
                  className="max-w-[90vw] max-h-[90vh] object-contain"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}

            {/* Property Info */}
            <div className="mb-8">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-4">
                <div className="min-w-0 flex-1">
                  <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-navy mb-2 break-words">
                    {property.title}
                  </h1>
                  <div className="flex items-start gap-2 text-slate-500">
                    <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span className="break-words">{property.address}</span>
                  </div>
                </div>
                <div className="sm:text-right shrink-0">
                  <div className="text-2xl sm:text-3xl font-heading font-bold text-navy">
                    {formatPrice(property.price)}
                  </div>
                  <div className="text-slate-500 text-sm sm:text-base">per month</div>
                </div>
              </div>

              <div className="flex flex-wrap gap-x-6 gap-y-3 py-4 border-y border-slate-200">
                <div className="flex items-center gap-2">
                  <Bed className="w-5 h-5 text-navy" />
                  <span className="text-slate-600 text-sm sm:text-base">{property.bedrooms} {property.bedrooms === 1 ? "Bedroom" : "Bedrooms"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Bath className="w-5 h-5 text-navy" />
                  <span className="text-slate-600 text-sm sm:text-base">{property.bathrooms} {property.bathrooms === 1 ? "Bathroom" : "Bathrooms"}</span>
                </div>
                <Badge variant="secondary" className="bg-gold/10 text-gold">
                  {property.location}
                </Badge>
              </div>
            </div>

            {/* Description */}
            <div className="mb-8">
              <h2 className="font-heading text-xl font-semibold text-navy mb-4">Description</h2>
              <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">
                {property.description}
              </p>
            </div>

            {/* Features */}
            {property.features?.length > 0 && (
              <div>
                <h2 className="font-heading text-xl font-semibold text-navy mb-4">Key Features</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {property.features.map((feature, index) => (
                    <div key={`${feature}-${index}`} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                      <span className="text-slate-600 text-sm sm:text-base">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Enquiry Form */}
          <div>
            <Card className="lg:sticky lg:top-28 border-slate-200">
              <CardContent className="p-4 sm:p-6">
                <h3 className="font-heading text-lg sm:text-xl font-semibold text-navy mb-2">
                  Enquire About This Property
                </h3>
                <p className="text-slate-500 text-sm mb-6">
                  We aim to respond promptly during business hours.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Input
                      placeholder="Your Name *"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="enquiry-name"
                    />
                  </div>
                  <div>
                    <Input
                      type="email"
                      placeholder="Email Address *"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="enquiry-email"
                    />
                  </div>
                  <div>
                    <Input
                      type="tel"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      data-testid="enquiry-phone"
                    />
                  </div>
                  <div>
                    <Textarea
                      placeholder="Your Message *"
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      data-testid="enquiry-message"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-navy hover:bg-navy-light"
                    disabled={submitting}
                    data-testid="enquiry-submit"
                  >
                    {submitting ? "Submitting..." : "Enquire Now"}
                  </Button>
                </form>

                <div className="mt-6 pt-6 border-t border-slate-200 text-center">
                  <p className="text-slate-500 text-sm mb-2">Or call us directly</p>
                  <a
                    href="tel:01924 520163"
                    className="inline-flex items-center gap-2 text-navy font-semibold hover:text-gold transition-colors"
                  >
                    <Phone className="w-5 h-5" />
                    01924 520163
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetailPage;
